import { createSignal, onCleanup } from "solid-js";

import { applyRubberBand, createGestureRecognizer } from "@/lib/gestures/recognizer";
import { vibrate } from "@/lib/gestures/haptics";
import { motionDurationMs } from "@/lib/gestures/reduced-motion";
import { pointerHandlers } from "@/lib/gestures/solid";
import { MENU_CATEGORY_SWIPE } from "@/lib/gestures/presets";

interface MenuCategorySwipeOptions {
  /** Current active page index. */
  index: () => number;
  /** Number of pages (categories, including the "All" tab). */
  count: () => number;
  /** Commit a page change to `next` (already clamped to a valid index). */
  onChange: (next: number) => void;
  /** The element that owns the gesture, for the page-width commit ratio. */
  getSurfaceRef: () => HTMLElement | undefined;
}

const {
  thresholdPx: THRESHOLD_PX,
  commitThresholdPx: COMMIT_THRESHOLD_PX,
  commitThresholdRatio: COMMIT_THRESHOLD_RATIO,
  rubberBandFactor: RUBBER_BAND_FACTOR,
  hapticMs: HAPTIC_MS,
  transitionMs: TRANSITION_MS,
  fallbackWidthPx: FALLBACK_WIDTH_PX,
  fling: FLING,
} = MENU_CATEGORY_SWIPE;

/**
 * Swipe-to-page between menu category tabs in the POS product grid — a
 * bidirectional carousel built on the shared recognizer (same axis-lock,
 * deadzone and fling physics as the POS/KDS row swipes and the canon calendar).
 *
 * Swiping left pages to the next category, right to the previous. The grid
 * tracks the finger; on commit (or fling) the current page slides fully out and
 * `onChange` advances the index, after which the new page renders at rest. At
 * the first/last category there is no neighbour, so over-drag rubber-bands and
 * snaps back. Tabs and the keyboard remain the primary, fully accessible path —
 * this gesture is additive.
 */
export function useMenuCategorySwipe(options: MenuCategorySwipeOptions) {
  const [dragX, setDragX] = createSignal(0);
  const [isAnimating, setIsAnimating] = createSignal(false);
  let swiping = false;

  const pageWidth = () => options.getSurfaceRef()?.clientWidth ?? FALLBACK_WIDTH_PX;

  /** Whether a page exists in the given travel direction (dx<0 → next). */
  const hasNeighbour = (dx: number): boolean => {
    if (dx < 0) {
      return options.index() < options.count() - 1;
    }
    return options.index() > 0;
  };

  const snapBack = () => {
    setIsAnimating(true);
    setDragX(0);
    setTimeout(() => {
      setIsAnimating(false);
    }, motionDurationMs(TRANSITION_MS));
  };

  const commit = (dx: number) => {
    const delta = dx < 0 ? 1 : -1;
    const next = options.index() + delta;
    const width = pageWidth();
    setIsAnimating(true);
    setDragX(dx < 0 ? -width : width);
    vibrate(HAPTIC_MS);
    setTimeout(() => {
      options.onChange(next);
      // Land the new page at rest without an animated jump.
      setIsAnimating(false);
      setDragX(0);
    }, motionDurationMs(TRANSITION_MS));
  };

  const recognizer = createGestureRecognizer({
    axis: "x",
    thresholdPx: THRESHOLD_PX,
    fling: FLING,
    onStart: () => {
      swiping = true;
      setIsAnimating(false);
    },
    onMove: (sample) => {
      setIsAnimating(false);
      // Rubber-band when there is no page to reveal in that direction.
      const dx = hasNeighbour(sample.dx)
        ? sample.dx
        : applyRubberBand(sample.dx, 0, RUBBER_BAND_FACTOR);
      setDragX(dx);
    },
    onEnd: (sample) => {
      const commitPx = Math.min(COMMIT_THRESHOLD_PX, pageWidth() * COMMIT_THRESHOLD_RATIO);
      const committed = sample.isFling || Math.abs(sample.dx) >= commitPx;
      if (committed && hasNeighbour(sample.dx)) {
        commit(sample.dx);
      } else {
        snapBack();
      }
      // Clear tap-suppression after any click would have fired.
      queueMicrotask(() => {
        swiping = false;
      });
    },
    onCancel: () => {
      snapBack();
      swiping = false;
    },
  });

  const handlers = pointerHandlers(recognizer);

  onCleanup(() => {
    recognizer.cancel();
  });

  return {
    dragX,
    isAnimating,
    /** True while a horizontal swipe is engaged (use to suppress a product tap). */
    isSwiping: () => swiping,
    handlePointerDown: handlers.onPointerDown,
    handlePointerMove: handlers.onPointerMove,
    handlePointerUp: handlers.onPointerUp,
    handlePointerCancel: handlers.onPointerCancel,
    TRANSITION_MS,
  };
}
